import { List } from "@refinedev/antd";
import { Button, Card, Divider, Form, InputNumber, Select, Space, Tag } from "antd";
import { useApiUrl, useNotification } from "@refinedev/core";
import axios from "axios";
import React, { useCallback, useState } from "react";
import {
  MAX_EMAIL_SIZE_CEILING_BYTES,
  MIN_EMAIL_SIZE_FLOOR_BYTES,
} from "../../constants/emailSize";

type ToggleTo = "permitted" | "not permitted";

// The API stores/enforces the floor in BYTES (email size on the wire). MB is
// a display/input convenience only -- convert at the edge, on read and on
// write, so the wire contract (`floorBytes`) never changes.
const BYTES_PER_MB = 1000000;
const MIN_EMAIL_SIZE_FLOOR_MB = MIN_EMAIL_SIZE_FLOOR_BYTES / BYTES_PER_MB;
const MAX_EMAIL_SIZE_CEILING_MB = MAX_EMAIL_SIZE_CEILING_BYTES / BYTES_PER_MB;

export const GlobalSettingsPage: React.FC = () => {
  const [toggleTo, setToggleTo] = useState<ToggleTo>("permitted");
  const [orderValueThreshold, setOrderValueThreshold] = useState<number>(500);
  const [form] = Form.useForm();

  const apiURL = useApiUrl();
  const { open } = useNotification();

  // Re-fetches the persisted settings and repopulates the form. Used once on
  // mount, and again after a successful save so the form displays what the
  // API actually stored, not just what was typed.
  const loadEmailSettings = useCallback(() => {
    const token = localStorage.getItem("emr-auth");
    return axios
      .get(`${apiURL}/email-settings`, {
        withCredentials: false,
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        form.setFieldsValue({
          floorMb:
            data?.floorBytes != null ? data.floorBytes / BYTES_PER_MB : undefined,
          chunkLimit: data?.chunkLimit,
        });
      })
      .catch(() => {
        open?.({
          type: "error",
          message: "Could not load email chunking settings",
          description: "Error",
        });
      });
  }, [apiURL, form, open]);

  // Load exactly once on mount. loadEmailSettings is deliberately omitted
  // from the deps array: its identity changes whenever `open` or `form`
  // change identity, which happens on ordinary re-renders (e.g. the
  // toggleTo/orderValueThreshold state updates below). Re-running the fetch
  // on those re-renders would call form.setFieldsValue and silently clobber
  // whatever the admin is mid-typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    loadEmailSettings();
  }, []);

  const onFinish = async (formData: {
    toggleTo: ToggleTo;
    orderValueThreshold: number;
    floorMb: number;
    chunkLimit: number;
  }) => {
    setToggleTo(formData?.toggleTo);
    setOrderValueThreshold(formData?.orderValueThreshold);

    // /task/toggle/automated/order and /task/update/order/threshold are now guarded
    // (AuthGuard + admin role); send the admin JWT like every other authenticated call.
    const token = localStorage.getItem("emr-auth");
    // Fractional MB must round to a whole number of bytes before it hits the wire.
    const floorBytes = Math.round(formData?.floorMb * BYTES_PER_MB);

    axios
      .all([
        axios.post(
          `${apiURL}/task/toggle/automated/order`,
          {
            toggleTo: formData?.toggleTo,
          },
          {
            withCredentials: false,
            headers: {
              "Access-Control-Allow-Origin": "*",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        axios.post(
          `${apiURL}/task/update/order/threshold`,
          {
            thresholdValue: formData?.orderValueThreshold,
          },
          {
            withCredentials: false,
            headers: {
              "Access-Control-Allow-Origin": "*",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
        axios.post(
          `${apiURL}/email-settings`,
          {
            floorBytes,
            chunkLimit: formData?.chunkLimit,
          },
          {
            withCredentials: false,
            headers: {
              "Access-Control-Allow-Origin": "*",
              Authorization: `Bearer ${token}`,
            },
          }
        ),
      ])
      .then(
        axios.spread((data1, data2, data3) => {
          // Re-read from the server so the form shows what was actually
          // persisted (not just the typed value) before confirming success.
          loadEmailSettings().then(() => {
            open?.({
              type: "success",
              message: `Updated application wide settings`,
              description: "Success",
            });
          });
        })
      )
      .catch(
        axios.spread((data1, data2, data3) => {
          open?.({
            type: "error",
            message: "Failed to update application wide settings",
            description: "Error",
          });
        })
      );
  };

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  };

  const tailLayout = {
    wrapperCol: { offset: 8, span: 16 },
  };

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "Global Settings",
        subTitle: "application wide settings",
        tags: <Tag color="green">live</Tag>,
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Form
        name="global_application_settings"
        form={form}
        {...layout}
        onFinish={onFinish}
        initialValues={{
          toggleTo: "permitted",
          orderValueThreshold: 500,
        }}
      >
        <Card style={{ width: "100%" }}>
          <Form.Item name="toggleTo" label="Automated Ordering">
            <Select value={toggleTo} style={{ width: "100%" }}>
              <Select.Option value="permitted">permitted</Select.Option>
              <Select.Option value="not permitted">not permitted</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="Order Value Threshold ($)"
            name="orderValueThreshold"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <InputNumber
              value={orderValueThreshold}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item {...tailLayout}>
            <Space>
              <Button type="primary" htmlType="submit">
                Submit
              </Button>
              <Button type="default" htmlType="button">
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Card>
        <Card title="Email Chunking" size="small" style={{ marginTop: 16 }}>
          <Form.Item
            label="Minimum Max Email Size (MB)"
            name="floorMb"
            tooltip="Vessels below this are raised to it nightly. This is the size of the email on the wire, not the attachment. It cannot exceed the per-vessel maximum of 50 MB."
          >
            {/* The max mirrors the per-vessel ceiling. The nightly job writes
                this value verbatim to every vessel below it, so a floor above
                that ceiling would push the whole fleet past a bound the
                per-vessel form enforces, and would leave that form's minimum
                above its maximum. Bounds are expressed in MB for the admin,
                but they correspond exactly to the API's byte bounds
                (MIN_EMAIL_SIZE_FLOOR_BYTES / MAX_EMAIL_SIZE_CEILING_BYTES) --
                antd's InputNumber only clamps out-of-range values rather than
                rejecting them, so these are a usability aid, not enforcement;
                the API is still the source of truth. */}
            <InputNumber
              style={{ width: "100%" }}
              min={MIN_EMAIL_SIZE_FLOOR_MB}
              max={MAX_EMAIL_SIZE_CEILING_MB}
              step={0.5}
            />
          </Form.Item>
          <Form.Item
            label="Maximum emails per update"
            name="chunkLimit"
            tooltip="An update needing more than this is not sent, and the vessel is emailed instead."
          >
            <InputNumber style={{ width: "100%" }} min={1} />
          </Form.Item>
        </Card>
      </Form>
    </List>
  );
};
